import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "./AuthContext";
import { decodeJwt, getUserFromToken, validateDomain } from "./jwt";
import {
  clearCodeVerifier,
  clearOAuthState,
  exchangeCodeForTokens,
  getCodeVerifier,
  getProviderById,
  getProviders,
  getStoredProviderId,
  redirectToLogin,
  verifyOAuthState,
} from "./oidc";
import { tokenStore } from "./tokenStore";
import type {
  AuthCode,
  AuthUser,
  CodeVerifier,
  JwtPayload,
  OAuthState,
} from "./types";

type CallbackParams = {
  code: string;
  codeVerifier: CodeVerifier;
  providerId: string;
};

type ValidationError = {
  kind:
    | "missing_params"
    | "state_mismatch"
    | "missing_verifier"
    | "missing_provider";
  message: string;
};

function validateCallbackParams(
  searchParams: URLSearchParams,
): CallbackParams | ValidationError {
  const code = searchParams.get("code");
  const state = searchParams.get("state");

  if (!code || !state)
    return {
      kind: "missing_params",
      message: "Missing authorization parameters",
    };

  if (!verifyOAuthState(state as OAuthState)) {
    console.warn(
      "OAuth state verification failed; possible CSRF/state mismatch",
    );
    clearOAuthState();
    return {
      kind: "state_mismatch",
      message: "Login validation failed — please try again",
    };
  }

  const codeVerifier = getCodeVerifier();
  if (!codeVerifier) {
    clearOAuthState();
    return {
      kind: "missing_verifier",
      message: "Missing PKCE verifier — please try logging in again",
    };
  }

  const providerId = getStoredProviderId();
  if (!providerId) {
    clearOAuthState();
    return {
      kind: "missing_provider",
      message: "Missing provider context — please try logging in again",
    };
  }

  return { code, codeVerifier, providerId };
}

function persistTokens(
  accessToken: string,
  refreshToken?: string,
  idToken?: string,
): void {
  tokenStore.setAccessToken(accessToken);
  if (refreshToken) tokenStore.setRefreshToken(refreshToken);
  if (idToken) tokenStore.setIdToken(idToken);
  else tokenStore.clearIdToken();
}

function decodeIdentityToken(tokens: {
  id_token?: string;
  access_token: string;
}): JwtPayload {
  const jwt = tokens.id_token ?? tokens.access_token;
  const payload = decodeJwt(jwt);
  if (!payload) throw new Error("Failed to decode identity token");
  return payload;
}

async function exchangeAndDecode(
  code: string,
  codeVerifier: CodeVerifier,
  providerId: string,
): Promise<{
  user: AuthUser;
  accessToken: string;
  tokens: { refresh_token?: string; id_token?: string };
}> {
  const provider = getProviderById(providerId);
  if (!provider) throw new Error(`Unknown OIDC provider: ${providerId}`);

  const tokens = await exchangeCodeForTokens(
    provider,
    code as AuthCode,
    codeVerifier,
  );
  clearCodeVerifier();

  const payload = decodeIdentityToken(tokens);

  if (!validateDomain(payload, provider.allowedDomains)) {
    throw new Error("Your account is not allowed to access this application");
  }

  const user = getUserFromToken(payload);
  if (!user) throw new Error("Failed to extract user from token");

  return {
    user,
    accessToken: tokens.access_token,
    tokens: { refresh_token: tokens.refresh_token, id_token: tokens.id_token },
  };
}

export function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const context = useContext(AuthContext);
  const processingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    async function processCallback() {
      const result = validateCallbackParams(searchParams);
      if ("kind" in result) {
        if (result.kind === "missing_params") {
          const providers = getProviders();
          const first = providers[0];
          if (first) {
            redirectToLogin(first).catch((err) => {
              console.error("Failed to redirect to login:", err);
              setError(result.message);
            });
          } else {
            setError(result.message);
          }
          return;
        }
        setError(result.message);
        return;
      }

      try {
        if (!context)
          throw new Error("AuthCallback must be used within AuthProvider");

        const { user, accessToken, tokens } = await exchangeAndDecode(
          result.code,
          result.codeVerifier,
          result.providerId,
        );
        tokenStore.setProviderId(result.providerId);
        persistTokens(accessToken, tokens.refresh_token, tokens.id_token);
        clearOAuthState();
        context.completeLogin(user, accessToken);
        navigate(tokenStore.getRedirectLocation(), { replace: true });
      } catch (err) {
        console.error("Token exchange failed:", err);
        clearOAuthState();
        const message =
          err instanceof Error ? err.message : "Authentication failed";
        setError(message);
      }
    }

    processCallback();
  }, [searchParams, navigate, context]);

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <h2>Authentication Error</h2>
        <p>{error}</p>
        <button
          type="button"
          onClick={() => {
            setError(null);
            processingRef.current = false;
            const providers = getProviders();
            const first = providers[0];
            if (first) {
              redirectToLogin(first).catch((err) => {
                console.error("Failed to redirect to login:", err);
                setError(
                  "Failed to redirect to login. Please check your connection and try again.",
                );
              });
            }
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <p>Completing authentication…</p>
    </div>
  );
}
